import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Users, UserCheck, Info } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { dayTimeOptions, PRIORITY_OPTIONS, PRIORITY_LABEL } from "../lib/time";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Dialog } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchableselect";
import { MultiSelect } from "../components/ui/multiselect";

const PRIO_TONE = { 1: "text-critical border-critical/40 bg-critical/10", 2: "text-warning border-warning/40 bg-warning/10", 3: "text-primary border-primary/40 bg-primary/10", 4: "text-muted border-line bg-surface2", 5: "text-faint border-line bg-surface2" };

export default function MasterWeekBuilder() {
  const { config, workstations, requirements, agents, professions, refreshLists } = useApp();
  const days = config?.days || [];
  const rows = workstations.filter((w) => !w.is_default);
  const wsById = Object.fromEntries(workstations.map((w) => [w.id, w]));
  const agentById = Object.fromEntries(agents.map((a) => [a.id, a]));
  const profById = Object.fromEntries(professions.map((p) => [p.id, p.name]));
  const timeOptions = dayTimeOptions(config);
  const profOptions = professions.map((p) => ({ value: p.id, label: p.name }));
  const agentOptions = agents.map((a) => ({ value: a.id, label: a.name }));

  const reqByCell = useMemo(() => {
    const m = {};
    requirements.forEach((r) => { (m[`${r.workstation_id}|${r.day}`] ||= []).push(r); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [requirements]);

  const [ctx, setCtx] = useState(null); // {mode, wsId, day, reqId}
  const [form, setForm] = useState(null);
  const [dup, setDup] = useState(null); // {req, days:[]}
  const [dragOver, setDragOver] = useState(null);
  const dragRef = useRef(null);

  const openNew = (wsId, day) => {
    setCtx({ mode: "new", wsId, day });
    setForm({ start_time: "08:00", end_time: "16:00", required_count: 1,
      priority: String(wsById[wsId]?.priority || 3), target_professions: [], target_agents: [] });
  };
  const openEdit = (r) => {
    setCtx({ mode: "edit", wsId: r.workstation_id, day: r.day, reqId: r.id });
    setForm({ start_time: r.start_time, end_time: r.end_time, required_count: r.required_count,
      priority: String(r.priority || wsById[r.workstation_id]?.priority || 3),
      target_professions: r.target_professions || [], target_agents: r.target_agents || [] });
  };

  const bodyFrom = (f, wsId, day) => ({
    workstation_id: wsId, day, start_time: f.start_time, end_time: f.end_time,
    required_count: Number(f.required_count), priority: Number(f.priority),
    target_professions: f.target_professions, target_agents: f.target_agents,
  });

  const save = async () => {
    try {
      const body = bodyFrom(form, ctx.wsId, ctx.day);
      if (ctx.mode === "edit") await Endpoints.updateRequirement(ctx.reqId, body);
      else await Endpoints.createRequirement(body);
      await refreshLists();
      setCtx(null);
      toast.success(ctx.mode === "edit" ? "Block updated" : "Block added", { description: "Regenerate the schedule to apply." });
    } catch (e) {
      toast.error("Could not save block", { description: e?.response?.data?.detail || e.message });
    }
  };

  const del = async (r) => { await Endpoints.deleteRequirement(r.id); await refreshLists(); toast.success("Block removed"); };

  const confirmDup = async () => {
    if (!dup.days.length) return toast.error("Pick at least one day");
    const r = dup.req;
    try {
      const res = await Endpoints.bulkRequirements({
        workstation_id: r.workstation_id, days: dup.days,
        start_time: r.start_time, end_time: r.end_time, required_count: r.required_count,
        priority: r.priority ?? null, target_professions: r.target_professions || [], target_agents: r.target_agents || [],
      });
      await refreshLists();
      setDup(null);
      toast.success(`Copied to ${res.count} day${res.count > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("Could not duplicate", { description: e?.response?.data?.detail || e.message });
    }
  };

  const onDrop = async (e, wsId, day) => {
    e.preventDefault();
    setDragOver(null);
    const r = dragRef.current;
    dragRef.current = null;
    if (!r) return;
    const copy = e.altKey || e.ctrlKey || e.metaKey;
    if (!copy && r.workstation_id === wsId && r.day === day) return;
    try {
      const body = bodyFrom(
        { start_time: r.start_time, end_time: r.end_time, required_count: r.required_count,
          priority: r.priority ?? wsById[wsId]?.priority ?? 3,
          target_professions: r.target_professions || [], target_agents: r.target_agents || [] },
        wsId, day);
      if (copy) await Endpoints.createRequirement(body);
      else await Endpoints.updateRequirement(r.id, body);
      await refreshLists();
      toast.success(copy ? "Block copied here" : "Block moved");
    } catch (err) {
      toast.error("Move failed", { description: err.message });
    }
  };

  const gridCols = { gridTemplateColumns: `180px repeat(${days.length}, minmax(150px, 1fr))` };

  return (
    <div className="animate-fadeup">
      <PageHeader title="Master Week Builder" subtitle="Author the standard week as a grid: rows are workstations, columns are days. Add blocks in any cell, drag to move them, hold Alt/⌘ while dropping to copy, or use a block's duplicate button to fan it across days." />

      <div className="flex items-center gap-2 mb-3 text-[11px] text-muted">
        <Info className="w-3.5 h-3.5 text-primary" />
        <span>Drag a block to move it · hold <kbd className="px-1 py-0.5 bg-surface2 border border-line rounded text-ink">Alt</kbd>/<kbd className="px-1 py-0.5 bg-surface2 border border-line rounded text-ink">⌘</kbd> while dropping to copy · click a block to edit.</span>
      </div>

      <div className="overflow-x-auto rounded-sm border border-line bg-surface">
        <div className="min-w-[1100px]">
          {/* header */}
          <div className="grid border-b border-line bg-surface2/60" style={gridCols}>
            <div className="px-3 py-2.5 text-[11px] uppercase tracking-wider text-muted">Workstation</div>
            {days.map((d) => (
              <div key={d.date} className="px-3 py-2.5 border-l border-line text-center">
                <div className="text-sm font-medium text-ink">{d.label}</div>
                <div className="mono text-[10px] text-faint">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
          {/* rows */}
          {rows.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-faint">No workstations yet — create some on the Workstations page.</div>
          )}
          {rows.map((w) => (
            <div key={w.id} className="grid border-b border-line last:border-0" style={gridCols} data-testid={`builder-row-${w.id}`}>
              <div className="px-3 py-3 flex items-start gap-2 bg-surface2/30">
                <span className="w-3 h-3 rounded-sm border border-black/40 mt-0.5 shrink-0" style={{ backgroundColor: w.color }} />
                <div className="min-w-0">
                  <div className="text-sm text-ink font-medium leading-tight">{w.name}</div>
                  <div className="text-[10px] text-faint">{PRIORITY_LABEL[w.priority]}</div>
                </div>
              </div>
              {days.map((d) => {
                const key = `${w.id}|${d.date}`;
                const blocks = reqByCell[key] || [];
                const isOver = dragOver === key;
                return (
                  <div key={d.date}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                    onDragLeave={() => setDragOver((k) => (k === key ? null : k))}
                    onDrop={(e) => onDrop(e, w.id, d.date)}
                    className={`border-l border-line p-1.5 space-y-1.5 min-h-[84px] transition-colors duration-100 ${isOver ? "bg-primary/10" : ""}`}
                    data-testid={`builder-cell-${w.id}-${d.label}`}>
                    {blocks.map((r) => {
                      const overnight = r.end_time <= r.start_time;
                      const prio = r.priority || w.priority;
                      return (
                        <div key={r.id} draggable
                          onDragStart={() => { dragRef.current = r; }}
                          onClick={() => openEdit(r)}
                          className="group rounded-sm border-l-[3px] bg-surface2 border border-line px-2 py-1.5 cursor-grab active:cursor-grabbing hover:border-lineFocus transition-colors duration-100"
                          style={{ borderLeftColor: w.color }}
                          data-testid={`builder-block-${r.id}`}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="mono text-[11px] text-ink">{r.start_time}–{r.end_time}{overnight && <span className="text-primary">+1d</span>}</span>
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-100 flex items-center gap-0.5">
                              <button onClick={(e) => { e.stopPropagation(); setDup({ req: r, days: [] }); }} className="p-0.5 text-muted hover:text-primary" data-testid={`builder-duplicate-${r.id}`}><Copy className="w-3 h-3" /></button>
                              <button onClick={(e) => { e.stopPropagation(); del(r); }} className="p-0.5 text-muted hover:text-critical" data-testid={`builder-delete-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <span className={`text-[9px] px-1 rounded border ${PRIO_TONE[prio]}`}>{PRIORITY_LABEL[prio]}</span>
                            <span className="mono text-[10px] text-muted">×{r.required_count}</span>
                            {(r.target_agents || []).length > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] text-success"><UserCheck className="w-2.5 h-2.5" />{r.target_agents.length}</span>}
                            {(r.target_professions || []).length > 0 && <span className="inline-flex items-center gap-0.5 text-[9px] text-warning"><Users className="w-2.5 h-2.5" />{r.target_professions.length}</span>}
                          </div>
                        </div>
                      );
                    })}
                    <button onClick={() => openNew(w.id, d.date)} data-testid={`builder-add-${w.id}-${d.label}`}
                      className="w-full flex items-center justify-center gap-1 py-1 rounded-sm border border-dashed border-line text-[11px] text-faint hover:text-ink hover:border-lineFocus transition-colors duration-100">
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* create / edit block modal */}
      <Dialog open={!!ctx} onClose={() => setCtx(null)}
        title={ctx ? `${ctx.mode === "edit" ? "Edit" : "New"} block · ${wsById[ctx.wsId]?.name} · ${days.find((d) => d.date === ctx.day)?.label}` : ""}
        testid="block-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setCtx(null)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} data-testid="save-block-btn">{ctx?.mode === "edit" ? "Save changes" : "Add block"}</Button></>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <SearchableSelect label="Start time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} options={timeOptions} testid="block-start-select" />
              <SearchableSelect label="End time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} options={timeOptions} testid="block-end-select" />
            </div>
            {form.end_time <= form.start_time && <p className="text-[11px] text-primary -mt-1">Crosses midnight — continues into the next day.</p>}
            <div className="grid grid-cols-2 gap-3">
              <Input label="Required agents" type="number" min="1" value={form.required_count} onChange={(e) => setForm({ ...form, required_count: e.target.value })} data-testid="block-count-input" />
              <Select label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={PRIORITY_OPTIONS} testid="block-priority-select" />
            </div>
            <MultiSelect label="Only these professions (optional)" values={form.target_professions} onChange={(v) => setForm({ ...form, target_professions: v })} options={profOptions} placeholder="Any profession" testid="block-professions-select" />
            <MultiSelect label="Nominate specific agents · meeting (optional)" values={form.target_agents} onChange={(v) => setForm({ ...form, target_agents: v })} options={agentOptions} placeholder="No one nominated" testid="block-agents-select" />
          </>
        )}
      </Dialog>

      {/* duplicate-to-days modal */}
      <Dialog open={!!dup} onClose={() => setDup(null)} title="Duplicate block to days" testid="duplicate-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setDup(null)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={confirmDup} data-testid="confirm-duplicate-btn">Copy to {dup?.days.length || 0} day{dup?.days.length === 1 ? "" : "s"}</Button></>}>
        {dup && (
          <div>
            <p className="text-xs text-muted mb-2">Copy this {dup.req.start_time}–{dup.req.end_time} block on <span className="text-ink">{wsById[dup.req.workstation_id]?.name}</span> to the selected days.</p>
            <div className="grid grid-cols-7 gap-1.5">
              {days.map((d) => {
                const on = dup.days.includes(d.date);
                return (
                  <button key={d.date} type="button" data-testid={`duplicate-day-${d.label}`}
                    onClick={() => setDup((s) => ({ ...s, days: on ? s.days.filter((x) => x !== d.date) : [...s.days, d.date] }))}
                    className={`h-9 rounded-sm border text-xs transition-colors duration-150 ${on ? "bg-primary/15 border-primary/40 text-ink" : "bg-surface2 border-line text-faint hover:border-lineFocus"}`}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
